import _ from 'lodash';
import qs from 'query-string';
import axios, { AxiosResponse, AxiosInstance } from 'axios';
import { AxiosRequestConfig } from 'axios';

export interface RestApiOptions {
  dataPath: string;
  baseUrl: string;
}

export type DefaultGetResponse<E> = E;

export interface DefaultUpdateOrCreateResponse<E> {
  message: string;
  data: E;
}

export interface DefaultGetAllResponse<E> {
  data: E[];
  current_page: number;
  first_page_url: string;
  from: number;
  last_page?: number;
  last_page_url?: string;
  next_page_url?: string;
  per_page: number | string;
  to: number;
  total?: number;
}

export class RestApi<
  E = unknown,
  GetAllResponse = DefaultGetAllResponse<E>,
  GetResponse = DefaultGetResponse<E>,
  PostResponse = DefaultUpdateOrCreateResponse<E>,
  PutResponse = DefaultUpdateOrCreateResponse<E>,
  PatchResponse = DefaultUpdateOrCreateResponse<E>,
  DeleteResponse = DefaultUpdateOrCreateResponse<E>
> {
  private options: RestApiOptions;

  public constructor(baseUrl: string, options?: Partial<RestApiOptions>) {
    this.options = {
      dataPath: 'data',
      baseUrl,
      ...options,
    };

    this.getApi = this.getApi.bind(this);
    this.processResponse = this.processResponse.bind(this);
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.put = this.put.bind(this);
    this.patch = this.patch.bind(this);
    this.getAll = this.getAll.bind(this);
    this.delete = this.delete.bind(this);
  }

  private getApi(): AxiosInstance {
    return axios.create({
      baseURL: this.options.baseUrl,
    });
  }

  public processResponse<T = E>(response: AxiosResponse<T>): T {
    return _.get(response, this.options.dataPath);
  }

  public async get(
    id: string | number,
    query: Parameters<typeof qs.stringify>[0] = {}
  ) {
    const res = await this.getApi().get(`${id}?${qs.stringify(query)}`);

    return this.processResponse<GetResponse>(res);
  }

  public async post<PostParams>(
    params?: PostParams,
    config?: AxiosRequestConfig
  ) {
    const res = await this.getApi().post(String(), params ?? {}, config);

    return this.processResponse<PostResponse>(res);
  }

  public async put<PutParams>(id: string | number, params?: PutParams) {
    const res = await this.getApi().put(id.toString(), params ?? {});

    return this.processResponse<PutResponse>(res);
  }

  public async patch<PatchParams>(params?: PatchParams) {
    const res = await this.getApi().patch(String(), params ?? {});

    return this.processResponse<PatchResponse>(res);
  }

  public async getAll<GetAllParams>(params?: GetAllParams) {
    const res = await this.getApi().get(String(), { params: params ?? {} });

    return this.processResponse<GetAllResponse>(res);
  }

  public async delete(id: string | number) {
    const res = await this.getApi().delete(id.toString());

    return this.processResponse<DeleteResponse>(res);
  }
}

export function createRestApi<
  E = unknown,
  GetAllResponse = DefaultGetAllResponse<E>,
  GetResponse = DefaultGetResponse<E>,
  PostResponse = DefaultUpdateOrCreateResponse<E>,
  PutResponse = DefaultUpdateOrCreateResponse<E>,
  PatchResponse = DefaultUpdateOrCreateResponse<E>,
  DeleteResponse = DefaultUpdateOrCreateResponse<E>
>(baseUrl: string, options?: Partial<RestApiOptions>) {
  return new RestApi<
    E,
    GetAllResponse,
    GetResponse,
    PostResponse,
    PutResponse,
    PatchResponse,
    DeleteResponse
  >(baseUrl, options);
}
